import { QueryClient } from '@tanstack/react-query'
import { registerQueryDefaults } from '@/admin/lib/query-persistence/query-defaults'

/** HTTP statuses a second attempt cannot change: auth / permission errors, and
 *  a route that is absent or flag-gated off for this tenant. */
const NON_RETRYABLE_STATUSES = new Set([401, 403, 404])

/**
 * Whether a failed query is worth retrying.
 *
 * Exported so the policy is testable - it is one line of behaviour that costs
 * real money when wrong. Leaving 404 out of the set meant every absent or
 * flag-gated route was requested three times; on the registrations page that
 * was ~3.3s of server time per load spent on requests that can never succeed.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status
  if (status !== undefined && NON_RETRYABLE_STATUSES.has(status)) return false
  return failureCount < 2
}

/**
 * The single admin-app QueryClient instance.
 *
 * Hoisted out of `App.tsx` into its own module so imperative, non-component
 * code (background cache warmers, route loaders) can call
 * `queryClient.prefetchQuery(...)` against the *same* client the React tree
 * uses. Importing this const does not couple the caller to React rendering.
 *
 * Per-key cache policies (list / detail / reference / context) are registered
 * here at module load, before any component mounts, so the defaults are in
 * place for the first query of every key - see the ordering note in
 * `query-defaults.ts`.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Keep inactive query data in memory for 30 min (up from the 5-min
      // default) so navigating away and back - or idling - does not evict a
      // list's rows and blank the table on return. Persistence then survives
      // full reloads. Per-key overrides live in registerQueryDefaults.
      gcTime: 30 * 60_000,
      retry: shouldRetryQuery,
    },
  },
})

registerQueryDefaults(queryClient)
